/**
 * The only vocabulary that crosses the iframe boundary.
 *
 * Both sides validate against this list and against an exact origin. No wildcard target origin
 * in either direction, and nothing carrying guest data: the parent page belongs to the hotel and
 * may be running any third-party script, so it learns that a booking completed and its reference,
 * never who booked or for how much.
 */

export const EMBED_MESSAGE_TYPES = [
  "exodus.booking.ready",
  "exodus.booking.resize",
  "exodus.booking.open",
  "exodus.booking.close",
  "exodus.booking.completed",
  "exodus.booking.origin_challenge",
  "exodus.booking.origin_proof",
] as const;

export type EmbedMessageType = (typeof EMBED_MESSAGE_TYPES)[number];

export type EmbedMessage =
  | { type: "exodus.booking.ready" }
  | { type: "exodus.booking.resize"; height: number }
  | { type: "exodus.booking.open" }
  | { type: "exodus.booking.close" }
  /** Flow reference only — never a name, an address or an amount. */
  | { type: "exodus.booking.completed"; flowId: string }
  | { type: "exodus.booking.origin_challenge"; challenge: string }
  | { type: "exodus.booking.origin_proof"; challenge: string };

export function isEmbedMessage(value: unknown): value is EmbedMessage {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as { type?: unknown; height?: unknown; flowId?: unknown; challenge?: unknown };
  if (typeof candidate.type !== "string") return false;
  if (!(EMBED_MESSAGE_TYPES as readonly string[]).includes(candidate.type)) return false;
  if (candidate.type === "exodus.booking.resize" && typeof candidate.height !== "number") return false;
  if (candidate.type === "exodus.booking.completed" && typeof candidate.flowId !== "string") return false;
  if (
    (candidate.type === "exodus.booking.origin_challenge" || candidate.type === "exodus.booking.origin_proof") &&
    (typeof candidate.challenge !== "string" || candidate.challenge.length < 16 || candidate.challenge.length > 160)
  ) return false;
  return true;
}

/**
 * Accepts only a bare https origin.
 *
 * Paths, wildcards and plain http are rejected rather than normalised — a hotel that configured
 * `https://*.example.com` should find out at setup time, not ship an embed that any subdomain
 * can frame.
 */
export function isAllowedOrigin(value: string | null | undefined): value is string {
  if (!value) return false;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.hostname !== "localhost" && url.hostname !== "127.0.0.1") return false;
    return url.pathname === "/" && !url.search && !url.hash && !url.hostname.includes("*");
  } catch {
    return false;
  }
}
